import { Activity, Users, MessageSquare, Sparkles, TrendingUp } from 'lucide-react';
import { founderAnalyticsService } from '../../lib/founder-analytics-service';
import { comparisonUtils } from '../../lib/comparison-utils';

export function EngagementTab({ data }: { data: any }) {
  if (!data || !data.engagement) {
    return (
      <div className="text-center py-12">
        <p className="text-[#6B7280]">No engagement data available</p>
      </div>
    );
  }

  const { engagement, feedback, aiUsage } = data;

  const dauComparison = comparisonUtils.compare(
    engagement.dau_today,
    engagement.dau_yesterday,
    true
  );

  const dauMauRatio = engagement.mau > 0
    ? ((engagement.dau_today / engagement.mau) * 100).toFixed(1)
    : '0.0';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-[#8CAFA8] to-[#7A9B94] p-6 rounded-[16px] text-white shadow-[0_2px_12px_rgba(140,175,168,0.3)]">
          <div className="flex items-center gap-3 mb-2">
            <Activity size={24} />
            <h3 className="text-sm font-medium opacity-90">DAU Today</h3>
          </div>
          <div className="text-4xl font-bold mb-2">
            {engagement.dau_today}
          </div>
          <div className={`text-sm ${dauComparison.isPositive ? 'opacity-90' : 'opacity-75'}`}>
            {comparisonUtils.getTrendIcon(dauComparison.trend)}{' '}
            {comparisonUtils.formatChangePercent(dauComparison)} vs yesterday
          </div>
        </div>

        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-3 mb-2">
            <Users className="text-[#8CAFA8]" size={24} />
            <h3 className="text-sm font-medium text-[#6B7280]">MAU</h3>
          </div>
          <div className="text-4xl font-bold text-[#1F2937] mb-2">
            {engagement.mau}
          </div>
          <div className="text-sm text-[#6B7280]">Monthly Active Users</div>
        </div>

        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="text-[#8CAFA8]" size={24} />
            <h3 className="text-sm font-medium text-[#6B7280]">DAU/MAU Ratio</h3>
          </div>
          <div className="text-4xl font-bold text-[#1F2937] mb-2">
            {dauMauRatio}%
          </div>
          <div className="text-sm text-[#6B7280]">Stickiness metric</div>
        </div>

        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-3 mb-2">
            <MessageSquare className="text-[#8CAFA8]" size={24} />
            <h3 className="text-sm font-medium text-[#6B7280]">Feedback (7d)</h3>
          </div>
          <div className="text-4xl font-bold text-[#1F2937] mb-2">
            {engagement.feedback_requests_7d}
          </div>
          <div className="text-sm text-[#6B7280]">Requests sent</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <h3 className="text-lg font-semibold text-[#1F2937] mb-4">Daily Active Users Trend</h3>
          <div className="space-y-4">
            <div className="p-4 bg-[#F9FAFB] rounded-[12px]">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-[#6B7280]">Today</span>
                <span className="text-3xl font-bold text-[#1F2937]">{engagement.dau_today}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#6B7280]">Yesterday</span>
                <span className="text-3xl font-bold text-[#6B7280]">{engagement.dau_yesterday}</span>
              </div>
            </div>
            <div className={`p-4 rounded-[12px] ${comparisonUtils.getTrendBackgroundColor(dauComparison.isPositive)}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Change</span>
                <span className={`text-xl font-bold ${comparisonUtils.getTrendColor(dauComparison.isPositive)}`}>
                  {comparisonUtils.formatChange(dauComparison)} users
                </span>
              </div>
              <div className={`text-sm mt-1 ${comparisonUtils.getTrendColor(dauComparison.isPositive)}`}>
                {comparisonUtils.formatChangePercent(dauComparison)} change
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <h3 className="text-lg font-semibold text-[#1F2937] mb-4">Engagement Metrics</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-[#E8F2F0] rounded-[12px]">
              <span className="text-[#374151]">Monthly Active Users</span>
              <span className="font-bold text-[#8CAFA8] text-lg">{engagement.mau}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-[#E8F2F0] rounded-[12px]">
              <span className="text-[#374151]">DAU/MAU Ratio</span>
              <span className="font-bold text-[#8CAFA8] text-lg">{dauMauRatio}%</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-[#D1FAE5] rounded-[12px]">
              <span className="text-[#374151]">Feedback Requests (7d)</span>
              <span className="font-bold text-[#065F46] text-lg">{engagement.feedback_requests_7d}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-orange-50 rounded-[12px]">
              <span className="text-[#374151]">Feedback Responses (7d)</span>
              <span className="font-bold text-orange-600 text-lg">{engagement.feedback_responses_7d}</span>
            </div>
          </div>
        </div>
      </div>

      {feedback && (
        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <h3 className="text-lg font-semibold text-[#1F2937] mb-4">Feedback Activity</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-[#F9FAFB] rounded-[12px]">
              <div className="text-sm text-[#6B7280] mb-1">Total Requests</div>
              <div className="text-3xl font-bold text-[#1F2937]">{feedback.total_requests}</div>
            </div>
            <div className="p-4 bg-[#E8F2F0] rounded-[12px]">
              <div className="text-sm text-[#6B7280] mb-1">Total Responses</div>
              <div className="text-3xl font-bold text-[#8CAFA8]">{feedback.total_responses}</div>
            </div>
            <div className="p-4 bg-[#E8F2F0] rounded-[12px]">
              <div className="text-sm text-[#6B7280] mb-1">Audio Responses</div>
              <div className="text-3xl font-bold text-[#8CAFA8]">{feedback.total_audio_responses}</div>
            </div>
            <div className="p-4 bg-[#D1FAE5] rounded-[12px]">
              <div className="text-sm text-[#065F46] mb-1">Response Rate</div>
              <div className="text-3xl font-bold text-[#065F46]">{feedback.overall_response_rate?.toFixed(1)}%</div>
            </div>
          </div>
        </div>
      )}

      {aiUsage && (
        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <h3 className="text-lg font-semibold text-[#1F2937] mb-4 flex items-center gap-2">
            <Sparkles className="text-[#8CAFA8]" size={20} />
            AI Summary Usage
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-[#E8F2F0] rounded-[12px]">
              <div className="text-sm text-[#6B7280] mb-1">Total Summaries</div>
              <div className="text-3xl font-bold text-[#8CAFA8]">{aiUsage.total_summaries}</div>
            </div>
            <div className="p-4 bg-[#E8F2F0] rounded-[12px]">
              <div className="text-sm text-[#6B7280] mb-1">Last 7 Days</div>
              <div className="text-3xl font-bold text-[#8CAFA8]">{aiUsage.summaries_last_7d}</div>
            </div>
            <div className="p-4 bg-[#D1FAE5] rounded-[12px]">
              <div className="text-sm text-[#065F46] mb-1">Last 30 Days</div>
              <div className="text-3xl font-bold text-[#065F46]">{aiUsage.summaries_last_30d}</div>
            </div>
            <div className="p-4 bg-orange-50 rounded-[12px]">
              <div className="text-sm text-orange-700 mb-1">Avg Responses</div>
              <div className="text-3xl font-bold text-orange-700">
                {aiUsage.avg_responses_per_summary?.toFixed(1) || '0'}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-[#E8F2F0] border border-[#8CAFA8]/30 p-6 rounded-[16px]">
        <h4 className="font-medium text-[#1F2937] mb-2">Engagement Insights</h4>
        <ul className="space-y-2 text-sm text-[#374151]">
          <li>
            DAU/MAU Ratio of {dauMauRatio}% indicates{' '}
            {parseFloat(dauMauRatio) > 20 ? 'strong' : parseFloat(dauMauRatio) > 10 ? 'moderate' : 'low'}{' '}
            user stickiness
          </li>
          {dauComparison.isPositive ? (
            <li>Daily active users are trending upward ({comparisonUtils.formatChangePercent(dauComparison)})</li>
          ) : (
            <li>Daily active users have declined ({comparisonUtils.formatChangePercent(dauComparison)})</li>
          )}
          {feedback && (
            <li>
              Feedback response rate is {feedback.overall_response_rate?.toFixed(1)}%
              {feedback.overall_response_rate > 50 ? ' (excellent)' : feedback.overall_response_rate > 30 ? ' (good)' : ' (needs improvement)'}
            </li>
          )}
          {aiUsage && aiUsage.summaries_last_7d > 0 && (
            <li>{aiUsage.summaries_last_7d} AI summaries generated in the last 7 days</li>
          )}
        </ul>
      </div>
    </div>
  );
}
